import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'

// Background images
import turbinFoto from '@/assets/backgrounds/turbin_foto.jpg'
import turbine5 from '@/assets/backgrounds/turbine_5.jpeg'
import turbineBlade1 from '@/assets/backgrounds/turbine_blade_1.jpeg'
import turbineBlade2 from '@/assets/backgrounds/turbine_blade_2.jpeg'
import turbines1 from '@/assets/backgrounds/turbines_1.jpeg'

interface BackgroundSliderProps {
  className?: string
  interval?: number // Değişim aralığı (ms)
  showDots?: boolean // Progress dots gösterilsin mi?
}

const BackgroundSlider: React.FC<BackgroundSliderProps> = ({ 
  className = '', 
  interval = 3000,
  showDots = true
}) => {
  const backgrounds = [
    {
      id: 1,
      image: turbinFoto,
      alt: 'Türbin Fotoğrafı'
    },
    {
      id: 2,
      image: turbine5,
      alt: 'Rüzgar Türbini'
    },
    {
      id: 3,
      image: turbineBlade1,
      alt: 'Türbin Kanadı 1'
    },
    {
      id: 4,
      image: turbineBlade2,
      alt: 'Türbin Kanadı 2'
    },
    {
      id: 5,
      image: turbines1,
      alt: 'Rüzgar Türbinleri'
    }
  ]

  const [currentIndex, setCurrentIndex] = useState(0)
  const [prevIndex, setPrevIndex] = useState(0)
  const isFirstRender = useRef(true)

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => {
        setPrevIndex(prev)
        // Aynı resim ard arda gösterilmesin
        const availableIndices = backgrounds
          .map((_, index) => index)
          .filter(index => index !== prev)
        
        const randomIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)]
        return randomIndex ?? 0
      })
    }, interval)

    return () => clearInterval(timer)
  }, [interval, backgrounds.length])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
    }
  }, [])

  const currentBackground = backgrounds[currentIndex]
  const prevBackground = backgrounds[prevIndex]

  if (!currentBackground) {
    return null
  }

  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`}>
      {/* Previous image layer - stays visible during transition */}
      <div
        className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${prevBackground?.image || currentBackground.image})`
        }}
      />
      
      {/* Current image layer - fades in on top */}
      <motion.div
        key={currentIndex}
        className="absolute inset-0 w-full h-full"
        initial={{ opacity: isFirstRender.current ? 1 : 0 }}
        animate={{ opacity: 1 }}
        transition={{ 
          duration: 1.5, 
          ease: "easeInOut"
        }}
      >
        <div
          className="w-full h-full bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${currentBackground.image})`
          }}
        />
      </motion.div>

      {/* Progress dots */}
      {showDots && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
          {backgrounds.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setPrevIndex(currentIndex)
                setCurrentIndex(index)
              }}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-white shadow-lg scale-110' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default BackgroundSlider 